/* eslint-disable padding-line-between-statements */
import _ from 'lodash';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useModal } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { TranslatorContext } from '@jutro/locale';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { AgencyAdminService } from "twia-capability-agencyadmin";
import AgentRegistrationService from '../../../../capabilities/ent-capability-agentregistration/services/AgentRegistrationService.js';
import metadata from './EditCommissionsDirectDeposit.metadata.json5';
import styles from './EditCommissionsDirectDeposit.module.scss';
import messages from './EditCommissionsDirectDeposit.messages.js';


function EditCommissionsDirectDeposit(props) {

    const { userVM } = props;

    const breakpoint = useContext(BreakpointTrackerContext)
    const { showAlert, showConfirm } = useModal();
    const { authHeader } = useAuthentication();
    const { onValidate, isComponentValid } = useValidation("EditCommissionsDirectDeposit");
    const [showErrors, setShowErrors] = useState(false);
    const isTWIA = _.get(userVM, 'associationName_TWIA.value.code') === 'twia';
    const viewModelService = useContext(ViewModelServiceContext);
    const [paymentPreferenceVM, setPaymentPreferenceVM] = useState();
    const { refreshData } = useDataRefresh();
    const history = useHistory();
    const checkImagePath = 'checkImages.value';
    const checkImages = _.get(paymentPreferenceVM, checkImagePath);
    const checkImage = _.get(checkImages, '[0]');
    const translator = useContext(TranslatorContext);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        AgencyAdminService.loadUnmaskedPaymentPreferences(authHeader).then(response => {

            if (!response.bankAccountType) {
                _.set(response, 'bankAccountType', 'checking')
            }

            const paymentPreferenceModel = viewModelService.create(response, 'pc', 'org.twia.elevate.edge.capabilities.agent.dto.PaymentPreferencesDTO', { 'UpdatePaymentPreferences_TWIA': true })

            paymentPreferenceModel.checkImages = null;
            setPaymentPreferenceVM(() => paymentPreferenceModel);
        })
    }, [viewModelService, authHeader]);

    // DE16808
    useEffect(() => {
        const myAccountInput = document.getElementsByName('confirmAccountNumber');

        if (myAccountInput && myAccountInput.length > 0) {
            myAccountInput[0].onpaste = () => false;
        }
    }, []);

    const getUWCompanyCode = useCallback(() => isTWIA ? '2001_00001' : '1001_00001', [isTWIA]);

    const uploadDocument = useCallback(
        async file => {
            const documentMetaDataTemplate = {
                docUID: '001',
                documentType: 'general_drop_file_ext',
                securityType: 'unrestricted',
                status: 'approved',
                author: 'Potential Agent',
                language: 'en_US',
                section: 'correspondence',
                tDIAgencyLicenseNumber_TFPA: `TDI-${_.get(userVM, 'organization_EXT.tdilicenseNumber.value')}`,
                uWCompany_ENT: getUWCompanyCode(),
                name: file.name,
                mimeType: file.type,
                sessionID: '-'
            };

            setLoading(true);

            try {
                const data = await AgentRegistrationService.uploadDocumentTFPA(
                    file,
                    documentMetaDataTemplate
                );
                const documentsArray = _.get(paymentPreferenceVM, checkImagePath) ?? [];

                documentsArray.push(data);
                _.set(paymentPreferenceVM, checkImagePath, documentsArray);
                setPaymentPreferenceVM(paymentPreferenceVM);
            } catch (error) {
                showAlert({
                    title: messages.documentUploadFailed,
                    message: messages.documentCannotUploaded,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            } finally {
                setLoading(false);
            }
        },
        [showAlert, setLoading, userVM, getUWCompanyCode, paymentPreferenceVM]
    );

    // US26488 retrieve token, then download, to avoid expiration
    const onDownloadDocument = useCallback(
        async (event, item) => {
            event.stopPropagation();

            if (_.isNil(item)) {
                return null;
            }

            setLoading(true);

            try {
                const agencyLicenseNumber = _.get(userVM, 'organization_EXT.tdilicenseNumber.value')
                const uwcompany = getUWCompanyCode()
                const docId = item.docUID;
                const token = await AgentRegistrationService.generateDownloadToken(
                    agencyLicenseNumber, docId, uwcompany
                );
                const downloadURL = AgentRegistrationService.getDocumentDownloadLink(docId, token);

                window.location.href = `${downloadURL}&licenseNumber=${agencyLicenseNumber}`;
            } catch (error) {
                showAlert({
                    title: messages.documentCannotUploaded,
                    message: messages.documentCannotUploaded,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            } finally {
                setLoading(false);
            }
        },
        [userVM, setLoading, getUWCompanyCode, showAlert]
    );

    // US26488 delete document functionality
    const onDeleteDocument = useCallback(
        (file, image) => {

            showConfirm({
                title: messages.removeCheckTitle,
                message: translator(messages.removeCheckMessage, {
                    documentname: image.name,
                }),
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.yesModel,
                cancelButtonText: messages.cancel,
                className: styles.jut__Button__filled,
            }).then(async (results) => {

                if (results === 'cancel' || results === 'close') {
                    return _.noop();
                }

                const agencyLicenseNumber = _.get(userVM, 'organization_EXT.tdilicenseNumber.value');
                const uwcompany = getUWCompanyCode()

                setLoading(true);
                AgentRegistrationService.deleteDocument(agencyLicenseNumber, image.docUID, uwcompany)
                    .then((result) => {
                        if (result) {
                            _.set(paymentPreferenceVM, checkImagePath, []);
                            setPaymentPreferenceVM(paymentPreferenceVM);
                        }
                    })
                    .catch(e => {
                        showAlert({
                            title: messages.documentCannotUploaded,
                            message: messages.documentCannotUploaded,
                            status: 'error',
                            icon: 'mi-error-outline',
                            confirmButtonText: commonMessages.ok,
                        }).catch(_.noop);
                    })
                    .finally(() => { setLoading(false); })
            }, _.noop)
        },
        [paymentPreferenceVM, userVM, getUWCompanyCode, showAlert, showConfirm, translator]);

    const resetBankAccountDetails = (() => {
        // DE16872
        const accountNumberValue = _.get(paymentPreferenceVM, 'accountNumber.value');
        if (_.isEmpty(accountNumberValue)) {
            _.set(paymentPreferenceVM, `accountNumber.value`, null);
            _.set(paymentPreferenceVM, `confirmAccountNumber.value`, null);
            _.set(paymentPreferenceVM, `routingNumber.value`, null);
            setPaymentPreferenceVM(paymentPreferenceVM);
            refreshData();
        }
    });

    const writeValue = useCallback(
        (value, path) => {
            _.set(paymentPreferenceVM, `${path}.value`, value);
            setPaymentPreferenceVM(paymentPreferenceVM);
            refreshData();
        },
        [paymentPreferenceVM, refreshData]
    );

    const cancel = () => {
        history.go('/administration');
    }

    const saveChanges = () => {
        if (!isComponentValid) {
            setShowErrors(true)

            return false
        }

        if (paymentPreferenceVM.checkImages.length === 1 || paymentPreferenceVM.accountNumber.value === null) {

            AgencyAdminService.updatePaymentPreferences(paymentPreferenceVM.value, authHeader).then(() => {
                history.go('/administration');
            }).catch(() => {
                showAlert({
                    title: messages.saveFailedTitle,
                    message: messages.saveFailedMsg,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            });
        } else {
            showAlert({
                title: messages.uploadDocumentTitle,
                message: messages.mustUploadDocumentMsg,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.close,
            }).catch(_.noop);
        }
    }

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            showErrors,
        },
        confirmAccountNumber: {
            showRequired: !!(_.get(paymentPreferenceVM, 'accountNumber.value')),
        },
        routingNumber: {
            showRequired: !!(_.get(paymentPreferenceVM, 'accountNumber.value'))
        },
        editDirectDepositTextTFPA: {
            visible: !isTWIA,
        },
        editDirectDepositTextTWIA: {
            visible: isTWIA
        },
        uploadCheckButton: {
            visible: checkImage === undefined && !loading
        },
        uploadDocumenItem: {
            visible: checkImage !== undefined && !loading
        },
        documentNameLink: {
            onClick: (event) => { onDownloadDocument(event, checkImage) },
            content: _.get(checkImage, 'name')
        },
        documentDeleteIcon: {
            onClick: (event) => { onDeleteDocument(event, checkImage) }
        },
        documentLoader: {
            visible: loading
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onUploadDocument: uploadDocument,
            onCancel: cancel,
            onSubmit: saveChanges,
            onResetBankAccountDetails: resetBankAccountDetails,
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={paymentPreferenceVM}
            overrideProps={overrideProps}
            onValidationChange={onValidate}
            classNameMap={resolvers.resolveClassNameMap}
            callbackMap={resolvers.resolveCallbackMap}
            onValueChange={writeValue}
        />
    );
}

EditCommissionsDirectDeposit.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func,
    }).isRequired,

};

EditCommissionsDirectDeposit.defaultProps = {

};

export default EditCommissionsDirectDeposit;
